import React, { useState, useEffect } from 'react';
import { buildingsAPI, floorsAPI, placesAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Building2,
  Layers,
  MapPin,
  Plus,
  Trash2,
  Loader2,
  ChevronRight
} from 'lucide-react';
import Layout from '../../components/Layout';
import { TranslatableSpan } from '../../components/TranslatableText';

const LocationsPage = () => {
  const [buildings, setBuildings] = useState([]);
  const [floors, setFloors] = useState([]);
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('buildings');
  
  // Dialog states
  const [buildingDialogOpen, setBuildingDialogOpen] = useState(false);
  const [floorDialogOpen, setFloorDialogOpen] = useState(false);
  const [placeDialogOpen, setPlaceDialogOpen] = useState(false);
  
  // Form states
  const [newBuilding, setNewBuilding] = useState({ name: '', address: '', description: '' });
  const [newFloor, setNewFloor] = useState({ name: '', building_id: '', level: 0, description: '' });
  const [newPlace, setNewPlace] = useState({ name: '', floor_id: '', place_type: '', description: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [buildingsRes, floorsRes, placesRes] = await Promise.all([
        buildingsAPI.getAll(),
        floorsAPI.getAll(),
        placesAPI.getAll()
      ]);
      setBuildings(buildingsRes.data);
      setFloors(floorsRes.data);
      setPlaces(placesRes.data);
    } catch (err) {
      toast.error('Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  // Building handlers
  const handleCreateBuilding = async () => {
    if (!newBuilding.name.trim()) {
      toast.error('Building name is required');
      return;
    }
    try {
      const response = await buildingsAPI.create(newBuilding);
      setBuildings([...buildings, response.data]);
      setNewBuilding({ name: '', address: '', description: '' });
      setBuildingDialogOpen(false);
      toast.success('Building created');
    } catch (err) {
      toast.error('Failed to create building');
    }
  };

  const handleDeleteBuilding = async (id) => {
    try {
      await buildingsAPI.delete(id);
      setBuildings(buildings.filter(b => b.id !== id));
      toast.success('Building deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete building');
    }
  };

  // Floor handlers
  const handleCreateFloor = async () => {
    if (!newFloor.name.trim() || !newFloor.building_id) {
      toast.error('Floor name and building are required');
      return;
    }
    try {
      const response = await floorsAPI.create(newFloor);
      setFloors([...floors, response.data]);
      setNewFloor({ name: '', building_id: '', level: 0, description: '' });
      setFloorDialogOpen(false);
      toast.success('Floor created');
    } catch (err) {
      toast.error('Failed to create floor');
    }
  };

  const handleDeleteFloor = async (id) => {
    try {
      await floorsAPI.delete(id);
      setFloors(floors.filter(f => f.id !== id));
      toast.success('Floor deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete floor');
    }
  };

  // Place handlers
  const handleCreatePlace = async () => {
    if (!newPlace.name.trim() || !newPlace.floor_id) {
      toast.error('Place name and floor are required');
      return;
    }
    try {
      const response = await placesAPI.create(newPlace);
      setPlaces([...places, response.data]);
      setNewPlace({ name: '', floor_id: '', place_type: '', description: '' });
      setPlaceDialogOpen(false);
      toast.success('Place created');
    } catch (err) {
      toast.error('Failed to create place');
    }
  };

  const handleDeletePlace = async (id) => {
    try {
      await placesAPI.delete(id);
      setPlaces(places.filter(p => p.id !== id));
      toast.success('Place deleted');
    } catch (err) {
      toast.error('Failed to delete place');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="locations-page">
        {/* Header */}
        <div>
          <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold uppercase tracking-tight text-white flex items-center gap-3">
            <Building2 className="w-10 h-10 text-amber-500" />
            Locations
          </h1>
          <p className="text-slate-400 mt-1">Manage buildings, floors, and places</p>
          <p className="text-slate-300 text-sm mt-3 max-w-3xl">
            Work orders can only be assigned to a place. After you add a building, add a floor, then add a place on that floor. The new place is what shows up in the work order location list.
          </p>
        </div>

        {/* Stats */}
        <div className="flex gap-4 flex-wrap">
          <div className="px-4 py-2 bg-slate-900/50 border border-slate-700 rounded-sm">
            <span className="text-slate-400 text-xs uppercase">Buildings</span>
            <span className="ml-2 text-white font-bold">{buildings.length}</span>
          </div>
          <div className="px-4 py-2 bg-slate-900/50 border border-slate-700 rounded-sm">
            <span className="text-slate-400 text-xs uppercase">Floors</span>
            <span className="ml-2 text-white font-bold">{floors.length}</span>
          </div>
          <div className="px-4 py-2 bg-slate-900/50 border border-slate-700 rounded-sm">
            <span className="text-slate-400 text-xs uppercase">Places</span>
            <span className="ml-2 text-white font-bold">{places.length}</span>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-slate-900 border border-slate-700">
            <TabsTrigger value="buildings" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">
              <Building2 className="w-4 h-4 mr-2" />
              Buildings
            </TabsTrigger>
            <TabsTrigger value="floors" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">
              <Layers className="w-4 h-4 mr-2" />
              Floors
            </TabsTrigger>
            <TabsTrigger value="places" className="data-[state=active]:bg-amber-500 data-[state=active]:text-white">
              <MapPin className="w-4 h-4 mr-2" />
              Places
            </TabsTrigger>
          </TabsList>

          {/* Buildings Tab */}
          <TabsContent value="buildings" className="mt-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white">Buildings</h2>
              <Dialog open={buildingDialogOpen} onOpenChange={setBuildingDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="add-building-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Building
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-slate-900 border-slate-700">
                  <DialogHeader>
                    <DialogTitle className="text-white">Add Building</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-slate-400">Name *</Label>
                      <Input
                        value={newBuilding.name}
                        onChange={(e) => setNewBuilding({ ...newBuilding, name: e.target.value })}
                        className="input-industrial mt-1"
                        placeholder="e.g., Main Building"
                        data-testid="building-name-input"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400">Address</Label>
                      <Input
                        value={newBuilding.address}
                        onChange={(e) => setNewBuilding({ ...newBuilding, address: e.target.value })}
                        className="input-industrial mt-1"
                        placeholder="e.g., 123 Main St"
                      />
                    </div>
                    <Button onClick={handleCreateBuilding} className="btn-primary w-full" data-testid="save-building-btn">
                      Create Building
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="space-y-3">
              {buildings.map((building) => (
                <div 
                  key={building.id}
                  className="card-industrial flex items-center justify-between"
                  data-testid={`building-${building.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-green-500/20 rounded-sm flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium"><TranslatableSpan text={building.name} /></p>
                      {building.address && <p className="text-slate-400 text-sm"><TranslatableSpan text={building.address} /></p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-400 text-sm">
                      {floors.filter(f => f.building_id === building.id).length} floors
                    </span>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button className="bg-red-500/20 hover:bg-red-500/30 text-red-400 p-2">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-slate-900 border-slate-700">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-white">Delete Building</AlertDialogTitle>
                          <AlertDialogDescription className="text-slate-400">
                            Delete {building.name}? You must delete all floors first.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDeleteBuilding(building.id)} className="bg-red-500 hover:bg-red-600 text-white">
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
              {buildings.length === 0 && (
                <div className="empty-state py-12">
                  <Building2 className="w-12 h-12 text-slate-600 mb-3" />
                  <p className="text-slate-400">No buildings yet. Add your first building.</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Floors Tab */}
          <TabsContent value="floors" className="mt-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white">Floors</h2>
              <Dialog open={floorDialogOpen} onOpenChange={setFloorDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="add-floor-btn" disabled={buildings.length === 0}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Floor
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-slate-900 border-slate-700">
                  <DialogHeader>
                    <DialogTitle className="text-white">Add Floor</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-slate-400">Building *</Label>
                      <Select value={newFloor.building_id} onValueChange={(v) => setNewFloor({ ...newFloor, building_id: v })}>
                        <SelectTrigger className="bg-slate-950 border-slate-700 text-white mt-1" data-testid="floor-building-select">
                          <SelectValue placeholder="Select building" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-700">
                          {buildings.map(b => (
                            <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-slate-400">Floor Name *</Label>
                      <Input
                        value={newFloor.name}
                        onChange={(e) => setNewFloor({ ...newFloor, name: e.target.value })}
                        className="input-industrial mt-1"
                        placeholder="e.g., Ground Floor, Level 1"
                        data-testid="floor-name-input"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400">Level Number</Label>
                      <Input
                        type="number"
                        value={newFloor.level}
                        onChange={(e) => setNewFloor({ ...newFloor, level: parseInt(e.target.value) || 0 })}
                        className="input-industrial mt-1"
                        placeholder="0"
                      />
                    </div>
                    <Button onClick={handleCreateFloor} className="btn-primary w-full" data-testid="save-floor-btn">
                      Create Floor
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="space-y-3">
              {floors.map((floor) => (
                <div 
                  key={floor.id}
                  className="card-industrial flex items-center justify-between"
                  data-testid={`floor-${floor.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-blue-500/20 rounded-sm flex items-center justify-center">
                      <Layers className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-slate-400 text-sm"><TranslatableSpan text={floor.building_name} /></span>
                        <ChevronRight className="w-3 h-3 text-slate-500" />
                        <p className="text-white font-medium"><TranslatableSpan text={floor.name} /></p>
                      </div>
                      <p className="text-slate-400 text-sm">Level {floor.level}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-400 text-sm">
                      {places.filter(p => p.floor_id === floor.id).length} places
                    </span>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button className="bg-red-500/20 hover:bg-red-500/30 text-red-400 p-2">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-slate-900 border-slate-700">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-white">Delete Floor</AlertDialogTitle>
                          <AlertDialogDescription className="text-slate-400">
                            Delete {floor.name}? You must delete all places first.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleDeleteFloor(floor.id)} className="bg-red-500 hover:bg-red-600 text-white">
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
              {floors.length === 0 && (
                <div className="empty-state py-12">
                  <Layers className="w-12 h-12 text-slate-600 mb-3" />
                  <p className="text-slate-400">No floors yet. Add a building first, then add floors.</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Places Tab */}
          <TabsContent value="places" className="mt-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white">Places</h2>
              <Dialog open={placeDialogOpen} onOpenChange={setPlaceDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="add-place-btn" disabled={floors.length === 0}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Place
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-slate-900 border-slate-700">
                  <DialogHeader>
                    <DialogTitle className="text-white">Add Place</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-slate-400">Floor *</Label>
                      <Select value={newPlace.floor_id} onValueChange={(v) => setNewPlace({ ...newPlace, floor_id: v })}>
                        <SelectTrigger className="bg-slate-950 border-slate-700 text-white mt-1" data-testid="place-floor-select">
                          <SelectValue placeholder="Select floor" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-700">
                          {floors.map(f => (
                            <SelectItem key={f.id} value={f.id}>
                              {f.building_name} / {f.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-slate-400">Place Name *</Label>
                      <Input
                        value={newPlace.name}
                        onChange={(e) => setNewPlace({ ...newPlace, name: e.target.value })}
                        className="input-industrial mt-1"
                        placeholder="e.g., Room 101, Conference Room A"
                        data-testid="place-name-input"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400">Type</Label>
                      <Select value={newPlace.place_type} onValueChange={(v) => setNewPlace({ ...newPlace, place_type: v })}>
                        <SelectTrigger className="bg-slate-950 border-slate-700 text-white mt-1">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-700">
                          <SelectItem value="room">Room</SelectItem>
                          <SelectItem value="office">Office</SelectItem>
                          <SelectItem value="corridor">Corridor</SelectItem>
                          <SelectItem value="bathroom">Bathroom</SelectItem>
                          <SelectItem value="kitchen">Kitchen</SelectItem>
                          <SelectItem value="storage">Storage</SelectItem>
                          <SelectItem value="parking">Parking</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button onClick={handleCreatePlace} className="btn-primary w-full" data-testid="save-place-btn">
                      Create Place
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="space-y-3">
              {places.map((place) => (
                <div 
                  key={place.id}
                  className="card-industrial flex items-center justify-between"
                  data-testid={`place-${place.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-purple-500/20 rounded-sm flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-slate-400"><TranslatableSpan text={place.building_name} /></span>
                        <ChevronRight className="w-3 h-3 text-slate-500" />
                        <span className="text-slate-400"><TranslatableSpan text={place.floor_name} /></span>
                        <ChevronRight className="w-3 h-3 text-slate-500" />
                        <p className="text-white font-medium"><TranslatableSpan text={place.name} /></p>
                      </div>
                      {place.place_type && (
                        <span className="text-slate-400 text-xs uppercase">{place.place_type}</span>
                      )}
                    </div>
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button className="bg-red-500/20 hover:bg-red-500/30 text-red-400 p-2">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-slate-900 border-slate-700">
                      <AlertDialogHeader>
                        <AlertDialogTitle className="text-white">Delete Place</AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-400">
                          Are you sure you want to delete {place.name}?
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDeletePlace(place.id)} className="bg-red-500 hover:bg-red-600 text-white">
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              ))}
              {places.length === 0 && (
                <div className="empty-state py-12">
                  <MapPin className="w-12 h-12 text-slate-600 mb-3" />
                  <p className="text-slate-400">No places yet. Add buildings and floors first.</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default LocationsPage;
